import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import PropTypes from 'prop-types';
import { ScrollView, Text, View } from 'react-native';
import { ActivityIndicator, Carousel, CancelableTextInput, CustomNavTitle } from 'LocalGluons';
import { t } from 'Locale';
import { useStore } from 'Store';
import { logEvent } from 'AppManager';
import { Device } from 'DeviceUtils';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';
import { MINIMUM_SEARCH_QUERY_LENGTH } from '../../../options';
import { iconPlaceholder } from '../../Navigation/navigationHeaderIcons';
import ContentWrapper from '../ContentWrapper';
import { groupV3SearchToSeries } from './utils';
import defaultStyles from './Search.jss';
import { show } from 'ReduxStore/webMenu';

const DEFAULT_QUERY_SCOPE = 'ALL';
const ACTORS_AND_DIRECTORS = 'CAST';
const DEFAULT_PACKAGE_SCOPE = 'SUBSCRIBED';
const postponeQueryTimeout = 150;

// eslint-disable-next-line max-statements
const Search = ({ navigation, route }) => {
    const { navigate, navigationProps, updateNavigationProps } = useNavigationPropsContext();
    const [showSearchBox, setShowSearchBox] = useState(
        navigationProps?.showSearchBox !== undefined ? navigationProps?.showSearchBox : true
    );
    const queryFromParams = route.params?.query || '';
    const queryScopeFromParams = route.params?.queryScope || '';
    const currentQuery = useRef(queryFromParams);
    const useQueryFromParams = useRef(Boolean(queryFromParams));
    const postponeQueryTimeoutHandler = useRef();
    const { appManager } = useStore();
    const { api: conector } = appManager;
    const [showInput, setShowInput] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchResult, setSearchResult] = useState({});
    const [textQuery, setTextQuery] = useState('');

    useEffect(() => {
        if (Device.isWeb) {
            setShowSearchBox(false);
        }
    }, []);

    useEffect(() => {
        if (navigationProps?.queryScope === ACTORS_AND_DIRECTORS) {
            setShowSearchBox(false);
        } else {
            setShowSearchBox(true);
        }
    }, [navigationProps?.queryScope]);

    const clearAll = () => {
        clearTimeout(postponeQueryTimeoutHandler.current);
        setSearchResult({});
        currentQuery.current = '';
        updateNavigationProps({ itemsHeaderCount: 0, queryScope: '' });
        setLoading(false);
    };

    const onQueryChange = async (query = '', scope) => {
        if (query && query.length >= MINIMUM_SEARCH_QUERY_LENGTH) {
            setLoading(true);
            clearTimeout(postponeQueryTimeoutHandler.current);
            currentQuery.current = query;
            postponeQueryTimeoutHandler.current = setTimeout(() => {
                logEvent({
                    type: 'navigation',
                    subtype: 'searchInit',
                    content1: query,
                    content2: scope || DEFAULT_QUERY_SCOPE
                });
                conector
                    .fetchSearchV3Extended({
                        query: scope === DEFAULT_QUERY_SCOPE ? `"*${query}*"` : `"${query}"`,
                        searchScope: scope || DEFAULT_QUERY_SCOPE,
                        DEFAULT_PACKAGE_SCOPE
                    })
                    .then((result) => {
                        if (currentQuery.current === query) {
                            logEvent({ type: 'navigation', subtype: 'searchOk', content1: 'OK' });
                            setSearchResult(result);
                            const {
                                movies: moviesWithGroupedSeries,
                                series: seriesWithGroupedSeries,
                                recordings: recordingsWithGroupedSeries,
                                futureTvPrograms: futureTvProgramsWithGroupedSeries,
                                archiveTvPrograms: archiveTvProgramsWithGroupedSeries,
                                liveTvPrograms: liveTvProgramsWithGroupedSeries
                            } = groupV3SearchToSeries(result);
                            updateNavigationProps({
                                itemsHeaderCount:
                                    moviesWithGroupedSeries?.length +
                                    seriesWithGroupedSeries?.length +
                                    recordingsWithGroupedSeries?.length +
                                    futureTvProgramsWithGroupedSeries?.length +
                                    archiveTvProgramsWithGroupedSeries?.length +
                                    liveTvProgramsWithGroupedSeries?.length
                            });
                            setLoading(false);
                        }
                    })
                    .catch((error) => {
                        logEvent({ type: 'navigation', subtype: 'searchNok', content1: error });
                        console.log(error);
                        setLoading(false);
                    });
            }, postponeQueryTimeout);
        } else {
            clearAll();
        }
    };

    useEffect(() => {
        onQueryChange(textQuery);
    }, [textQuery]);

    useEffect(() => {
        currentQuery.current = '';
        onQueryChange(queryFromParams, queryScopeFromParams || DEFAULT_QUERY_SCOPE);
    }, [queryFromParams]);

    const onChangeText = (text) => {
        // If user starts typing
        if (text.length > 0) {
            useQueryFromParams.current = false;
        }
        setTextQuery(text);
    };

    const onCancel = (textInputRef) => {
        textInputRef.clear();
        clearAll();
    };

    const onItemPress = useCallback((item) => {
        logEvent({
            type: 'navigation',
            subtype: 'searchConv',
            content1: `detail: ${item?.getId()}`
        });
        navigation.push('Details', {
            id: item?.getId(),
            type: item?.modelType,
            platform: item?.data?.pckg?.platform
        });
        updateNavigationProps({ item });
    }, []);

    const onViewAllPress = useCallback((title, showMetadata, _, items) => {
        logEvent({ type: 'navigation', subtype: 'searchConv', content1: `view all: ${title}` });
        navigate('ViewAll', { title, items, showMetadata });
    }, []);

    const logVieAllEvent = (title) => {
        logEvent({
            type: 'user-flow',
            subtype: 'search',
            content1: 'carousel-show-more',
            content2: title,
            context: '/view-all'
        });
    };

    const fetchLiveProgram = async (channelId) => {
        try {
            const now = Date.now();
            const response = await conector.fetchEpg({
                channelIds: channelId.toString(),
                from: now,
                to: now + 5 * 60 * 1000,
                offset: 0,
                limit: 10,
                queryScope: 'live'
            });
            const resultProgramItem = response.items[0].programs[0].data;
            return resultProgramItem;
        } catch (error) {
            console.log('in ProgramTimeline.js: fetchLiveProgram Error>         ', error);

            return null;
        } finally {
        }
    };

    const hasQuery = Boolean(currentQuery.current);
    const hasChannels = searchResult.channels?.length > 0;
    const hasActorsAndDirectors = searchResult.actorsAndDirectors?.length > 0;
    const hasFutureTvPrograms = searchResult.futureTvPrograms?.length > 0;
    const hasArchiveTvPrograms = searchResult.archiveTvPrograms?.length > 0;
    const hasLiveTvPrograms = searchResult.liveTvPrograms?.length > 0;
    const hasMovies = searchResult.movies?.length > 0;
    const hasSeries = searchResult.series?.length > 0;
    const hasRecordings = searchResult.recordings?.length > 0;

    const noResults =
        !hasChannels &&
        !hasActorsAndDirectors &&
        !hasLiveTvPrograms &&
        !hasArchiveTvPrograms &&
        !hasFutureTvPrograms &&
        !hasMovies &&
        !hasSeries &&
        !hasRecordings &&
        hasQuery;
    const {
        channels,
        actorsAndDirectors,
        liveTvPrograms,
        archiveTvPrograms,
        futureTvPrograms,
        movies,
        series,
        recordings
    } = groupV3SearchToSeries(searchResult);
    return (
        <View style={{ flex: 1 }}>
            <ContentWrapper keyboardShouldPersistTaps="always">
                {showSearchBox && (
                    <View style={defaultStyles.header}>
                        <CancelableTextInput
                            placeholder={t('SEARCH.INIT')}
                            onChangeText={onChangeText}
                            onCancel={onCancel}
                            displayCancelIcon={hasQuery}
                            {...(useQueryFromParams.current ? { value: queryFromParams } : {})}
                        />
                    </View>
                )}
                <View style={defaultStyles.container}>
                    {noResults && !loading ? (
                        <View style={defaultStyles.noResultsTextContainer}>
                            <Text style={defaultStyles.noResultsText}>
                                {t('COMMON.NO_RESULTS_FOUND')}
                            </Text>
                        </View>
                    ) : (
                        <>
                            {hasChannels && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-channles"
                                    testID="carousel-channles"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.TV_CHANNELS').toUpperCase()}
                                    items={channels}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={async (item) => {
                                        // onItemPress(item);
                                        const liveChannelId = item.data.data.channelId;
                                        const curentlyLiveItem =
                                            await fetchLiveProgram(liveChannelId);

                                        updateNavigationProps({
                                            searchParams: {
                                                curentlyLiveItem,
                                                liveChannelId
                                            }
                                        });
                                        // navigation.setParams({
                                        //     searchParams: {
                                        //         curentlyLiveItem,
                                        //         liveChannelId
                                        //     }
                                        // });

                                        navigate('TvGuide', {
                                            searchParams: {
                                                curentlyLiveItem,
                                                liveChannelId
                                            }
                                        });

                                        logEvent({
                                            type: 'user-flow',
                                            subtype: 'search',
                                            content1: 'carousel-open-detail',
                                            content2: 'TV STANICE',
                                            context: '/epg'
                                        });
                                    }}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasActorsAndDirectors && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-actorsAndDirectors"
                                    testID="carousel-actorsAndDirectors"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.ACTORS_AND_DIRECTORS').toUpperCase()}
                                    items={actorsAndDirectors}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={(item) => {
                                        navigate('Search', {
                                            showSearchBox: false,
                                            queryScope: ACTORS_AND_DIRECTORS,
                                            query: item.getTitle(),
                                            previousQuery: currentQuery.current,
                                            previousQueryScope: DEFAULT_QUERY_SCOPE
                                        });
                                        logEvent({
                                            type: 'user-flow',
                                            subtype: 'search',
                                            content1: 'carousel-open-detail',
                                            content2: 'HERCI A REŽISÉRI',
                                            context: '/search'
                                        });
                                    }}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasLiveTvPrograms && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-liveTvPrograms"
                                    testID="carousel-liveTvPrograms"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.LIVE_TV_PROGRAMS').toUpperCase()}
                                    items={liveTvPrograms}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={onItemPress}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasArchiveTvPrograms && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-archiveTvPrograms"
                                    testID="carousel-archiveTvPrograms"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.TV_ARCHIVE_PROGRAMS').toUpperCase()}
                                    items={archiveTvPrograms}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={onItemPress}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasFutureTvPrograms && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-futureTvPrograms"
                                    testID="carousel-futureTvPrograms"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.TV_FUTURE_PROGRAMS').toUpperCase()}
                                    items={futureTvPrograms}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={onItemPress}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasMovies && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-movies"
                                    testID="carousel-movies"
                                    style={defaultStyles.seriesCarousel}
                                    title={t('SEARCH.RESULTS.VOD_MOVIES').toUpperCase()}
                                    items={movies}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={(item) => onItemPress(item.data)}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasSeries && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-series"
                                    testID="carousel-series"
                                    style={defaultStyles.seriesCarousel}
                                    title={t('SEARCH.RESULTS.VOD_SERIES').toUpperCase()}
                                    items={series}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={(item) => onItemPress(item.data)}
                                    showCount
                                    showMetadata
                                />
                            )}
                            {hasRecordings && (
                                <Carousel
                                    keyboardShouldPersistTaps="always"
                                    key="carousel-recordings"
                                    testID="carousel-recordings"
                                    style={defaultStyles.carousel}
                                    title={t('SEARCH.RESULTS.RECORDINGS').toUpperCase()}
                                    items={recordings}
                                    onViewAllPress={(title, showMetadata, _, items) => {
                                        onViewAllPress(title, showMetadata, _, items);
                                        logVieAllEvent({ title });
                                    }}
                                    onItemPress={(item) => onItemPress(item)}
                                    showCount
                                    showMetadata
                                />
                            )}
                        </>
                    )}
                </View>
            </ContentWrapper>
            <View style={defaultStyles.activityIndicatorContainer} pointerEvents="none">
                {loading && <ActivityIndicator style={defaultStyles.activityIndicator} />}
            </View>
        </View>
    );
};

Search.propTypes = {
    navigation: PropTypes.shape({}).isRequired
};

// doesn't work since V5; need to pass with options in Navigator.screen
Search.navigationOptions = ({ navigation, route }) => {
    const { navigationProps } = useNavigationPropsContext();
    const count = navigationProps?.itemsHeaderCount || 0;
    const secondaryText = count > 0 ? ` (${count})` : null;

    return {
        headerTitle: () => (
            <CustomNavTitle
                primaryText={t('COMMON.SEARCH').toUpperCase()}
                secondaryText={secondaryText}
            />
        ),
        headerRight: () => iconPlaceholder
    };
};

export default Search;
